<script setup lang="ts">
// Large countdown timer shown in the top-right of the game screen.
// Tap the timer to toggle pause/resume.
// The component always occupies its layout space to prevent the sidebar
// from shifting when the timer appears/disappears.

const store = useChallengeWheelStore()
const props = withDefaults(defineProps<{
  interactive?: boolean
}>(), {
  interactive: true,
})

const showTimer = computed(() =>
  store.phase === 'playing' && store.currentMiniGameDuration > 0,
)

const formatted = computed(() => {
  const s = store.timerSeconds
  const m = Math.floor(s / 60)
  const sec = s % 60
  return m > 0 ? `${m}:${sec.toString().padStart(2, '0')}` : String(s)
})

const isWarning = computed(() => store.timerSeconds <= 10 && store.timerSeconds > 0)
const isExpired = computed(() => store.timerSeconds === 0 && store.phase === 'playing')
</script>

<template>
  <!--
    Always rendered at full size so the sidebar never shifts.
    Invisible + non-interactive when the timer is not active.
  -->
  <div
    v-click="showTimer && props.interactive ? store.toggleTimer : undefined"
    :class="[
      'timer-box flex h-28 w-full items-center justify-center rounded-xl select-none touch-none transition-opacity duration-300',
      showTimer ? (props.interactive ? 'opacity-100 cursor-pointer' : 'opacity-100 cursor-default') : 'opacity-0 pointer-events-none',
      isWarning ? 'bg-red-600/80 ring-2 ring-red-300' : 'bg-slate-800/80',
    ]"
  >
    <!-- :key re-mounts the span on each second tick, restarting the flash animation -->
    <span
      :key="store.timerSeconds"
      :class="[
        'timer-text font-black tabular-nums leading-none',
        isExpired ? 'text-red-400' : 'text-white',
        isWarning ? 'animate-timer-tick' : '',
      ]"
    >{{ formatted }}</span>
  </div>
</template>

<style scoped>
/*
  Match the team-box visual: same height (h-28 / 7rem), same
  size-container pattern so the digits scale via cqh/cqi. The
  font cap is generous because the timer is a single row of
  numbers — wider when it's mm:ss, but the cqi cap keeps it
  inside the box at any width.
*/
.timer-box {
  container-type: size;
  container-name: timer;
}
.timer-text {
  font-size: clamp(1.5rem, min(80cqh, 32cqi), 5rem);
}

/* Starts at peak brightness on mount (= on each second tick), dims toward end of the second */
@keyframes timer-tick {
  0%   { opacity: 1; text-shadow: 0 0 20px rgba(255,255,255,0.9); }
  70%  { opacity: 0.85; text-shadow: none; }
  100% { opacity: 0.55; text-shadow: none; }
}
.animate-timer-tick {
  animation: timer-tick 1s linear forwards;
}
</style>
